import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FiMail } from 'react-icons/fi';
import { QUERY_KEY } from '../../../../../services/queryEndPoint';
import { fetchLeaguePlayers } from '../../../players/api/playersApi';
import {
  fetchTournamentWaiverSignatures,
  remindAllUnsignedPlayers,
  remindPlayerToSignWaiver,
} from './waiverSignatureApi';
import '../../../waiver/WaiverManagement.scss';
import './MyWaiverTab.scss';

interface Props {
  tournamentId: string;
  leagueId: string;
  tournamentName?: string | null;
}

interface RosterRow {
  playerId: string;
  playerName: string;
  teamName: string | null;
  signed: boolean;
}

// Tournament rosters are small enough (a handful of teams) that paging through a few pages of the
// players/search endpoint client-side is simpler than adding a dedicated "full roster" endpoint.
const ROSTER_PAGE_SIZE = 100;
const MAX_ROSTER_PAGES = 10;

// Mirrors the backend's Valkey-based cooldowns (WaiverReminderThrottleService) so the UI doesn't
// invite a click that the API will just reject — the backend stays the source of truth (this is
// only a UX front-run of it), so a mismatch here just means one extra rejected request, not a
// correctness issue. Persisted to localStorage (per browser/device only) so a page refresh doesn't
// forget a reminder was just sent and re-offer the button.
const PLAYER_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const BULK_COOLDOWN_MS = 5 * 60 * 1000;

function playerCooldownKey(tournamentId: string, playerId: string): string {
  return `waiver-reminder-cooldown:${tournamentId}:${playerId}`;
}

function bulkCooldownKey(tournamentId: string): string {
  return `waiver-reminder-bulk-cooldown:${tournamentId}`;
}

function readTimestamp(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    const ts = raw ? Number(raw) : NaN;
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

function writeTimestamp(key: string): void {
  try {
    localStorage.setItem(key, String(Date.now()));
  } catch {
    // Private browsing / storage disabled — cooldown just won't persist across reloads or tabs.
  }
}

function remainingCooldownMs(key: string, windowMs: number, now: number): number {
  const lastSentAt = readTimestamp(key);
  return lastSentAt ? lastSentAt + windowMs - now : 0;
}

function formatRemaining(ms: number): string {
  const totalMinutes = Math.max(1, Math.ceil(ms / 60000));
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

function extractErrorMessage(error: unknown, fallback: string): string {
  const ax = error as { response?: { data?: { message?: string } } };
  return ax?.response?.data?.message || fallback;
}

export default function AdminWaiverPanel({ tournamentId, leagueId, tournamentName }: Props) {
  const queryClient = useQueryClient();
  const [remindingPlayerId, setRemindingPlayerId] = useState<string | null>(null);
  // Re-renders periodically purely so an expired cooldown re-enables its button on its own,
  // without requiring the admin to click something else first.
  const [, setCooldownTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setCooldownTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const { data: roster, isLoading: isLoadingRoster } = useQuery({
    queryKey: [QUERY_KEY.leaguePlayersSearch, leagueId, tournamentId, 'admin-waiver-roster'],
    queryFn: async () => {
      const all: { playerId: string; name: string; teamName: string | null }[] = [];
      for (let page = 0; page < MAX_ROSTER_PAGES; page += 1) {
        const result = await fetchLeaguePlayers(leagueId, { tournamentId, page, size: ROSTER_PAGE_SIZE });
        for (const p of result.content) {
          all.push({
            playerId: p.playerId,
            name: `${p.playerFirstName ?? ''} ${p.playerLastName ?? ''}`.trim() || p.playerCode || '-',
            teamName: p.teamName,
          });
        }
        if (!result.hasNext) break;
      }
      return all;
    },
    enabled: !!leagueId && !!tournamentId,
  });

  const { data: signatures, isLoading: isLoadingSignatures } = useQuery({
    queryKey: [QUERY_KEY.tournamentWaiverSignature, tournamentId, 'signatures'],
    queryFn: () => fetchTournamentWaiverSignatures(tournamentId),
    enabled: !!tournamentId,
  });

  const rows = useMemo<RosterRow[]>(() => {
    if (!roster) return [];
    const signedIds = new Set((signatures ?? []).map((s) => s.playerId));
    const seen = new Set<string>();
    const deduped: RosterRow[] = [];
    for (const p of roster) {
      if (seen.has(p.playerId)) continue;
      seen.add(p.playerId);
      deduped.push({ playerId: p.playerId, playerName: p.name, teamName: p.teamName, signed: signedIds.has(p.playerId) });
    }
    // Pending players first, so the admin sees who still needs a nudge without scrolling.
    return deduped.sort((a, b) => Number(a.signed) - Number(b.signed));
  }, [roster, signatures]);

  const pendingCount = rows.filter((r) => !r.signed).length;

  const remindOneMutation = useMutation({
    mutationFn: (playerId: string) => remindPlayerToSignWaiver(tournamentId, playerId),
    onMutate: (playerId) => setRemindingPlayerId(playerId),
    onSuccess: (result, playerId) => {
      // Record the attempt whether it actually sent or came back throttled/failed — either way an
      // instant re-click would just be rejected again, so start the cooldown regardless.
      writeTimestamp(playerCooldownKey(tournamentId, playerId));
      toast[result.sent ? 'success' : 'error'](
        result.sent ? 'Reminder email sent.' : result.failureReason || 'Could not send reminder email.'
      );
    },
    onError: (error) => toast.error(extractErrorMessage(error, 'Failed to send reminder. Please try again.')),
    onSettled: () => setRemindingPlayerId(null),
  });

  const remindAllMutation = useMutation({
    mutationFn: () => remindAllUnsignedPlayers(tournamentId),
    onSuccess: (results) => {
      writeTimestamp(bulkCooldownKey(tournamentId));
      const sentCount = results.filter((r) => r.sent).length;
      if (results.length === 0) {
        toast.success('No pending waivers to remind.');
      } else {
        toast.success(`Sent ${sentCount} of ${results.length} reminder email(s).`);
      }
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY.tournamentWaiverSignature, tournamentId] });
    },
    onError: (error) => toast.error(extractErrorMessage(error, 'Failed to send reminders. Please try again.')),
  });

  const isLoading = isLoadingRoster || isLoadingSignatures;
  const now = Date.now();
  const bulkRemainingMs = remainingCooldownMs(bulkCooldownKey(tournamentId), BULK_COOLDOWN_MS, now);
  const isBulkCoolingDown = bulkRemainingMs > 0;

  return (
    <div className="admin-waiver-panel">
      <div className="admin-waiver-panel-header">
        <div>
          <h2>Manage Waivers</h2>
          <p>
            {tournamentName ?? 'This tournament'} — {pendingCount} of {rows.length} player
            {rows.length === 1 ? '' : 's'} pending
          </p>
        </div>
        <button
          type="button"
          className="waivers-btn waivers-btn--primary"
          disabled={remindAllMutation.isPending || pendingCount === 0 || isBulkCoolingDown}
          title={isBulkCoolingDown ? `Reminders were just sent — try again in ${formatRemaining(bulkRemainingMs)}` : undefined}
          onClick={() => remindAllMutation.mutate()}
        >
          <FiMail size={16} />
          {remindAllMutation.isPending
            ? 'Sending…'
            : isBulkCoolingDown
              ? `Reminded — wait ${formatRemaining(bulkRemainingMs)}`
              : `Remind All Pending (${pendingCount})`}
        </button>
      </div>

      {isLoading ? (
        <p className="admin-waiver-empty">Loading roster…</p>
      ) : rows.length === 0 ? (
        <p className="admin-waiver-empty">No players registered for this tournament yet.</p>
      ) : (
        <div className="waivers-list-wrap">
          <table className="waivers-list-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>Team</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const playerRemainingMs = row.signed
                  ? 0
                  : remainingCooldownMs(playerCooldownKey(tournamentId, row.playerId), PLAYER_COOLDOWN_MS, now);
                const isPlayerCoolingDown = playerRemainingMs > 0;
                const isSendingThis = remindOneMutation.isPending && remindingPlayerId === row.playerId;
                return (
                  <tr key={row.playerId}>
                    <td className="waivers-list-name">{row.playerName}</td>
                    <td className="waivers-list-value">{row.teamName ?? '-'}</td>
                    <td>
                      <span className={`my-waiver-status-pill my-waiver-status-pill--${row.signed ? 'completed' : 'pending'}`}>
                        {row.signed ? 'Signed' : 'Pending'}
                      </span>
                    </td>
                    <td>
                      {!row.signed && (
                        <div className="waivers-list-actions">
                          {isPlayerCoolingDown ? (
                            <span className="admin-waiver-reminded-hint" title={`Wait ${formatRemaining(playerRemainingMs)} before reminding again`}>
                              Reminded
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="waivers-list-action-btn"
                              title="Send reminder email"
                              disabled={isSendingThis}
                              onClick={() => remindOneMutation.mutate(row.playerId)}
                            >
                              <FiMail size={16} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
